/**
 * Import-from-GitLab modal.
 *
 * Two-step UX:
 *   1. List the user's GitLab repos (paginated server-side, search-by-name client-side).
 *      Click a row to select it.
 *   2. Confirm form: project name (defaulted from repo name), initial prompt
 *      (defaulted to "Show me what's in this codebase and suggest where to start.").
 *      Submit → POST /v1/projects with `gitlabSource` populated.
 *
 * On success, parent navigates to the new project. The first build will
 * trigger the worker to clone the repo into the workspace (see
 * apps/worker/src/claude-runner.ts:cloneGitlabRepo).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ArrowLeft, GitBranch, Search } from 'lucide-react'
import type { ProjectDto } from '@doable/shared-types'
import { api, type ApiError, type GitlabRepoDto } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { Dialog } from './ui/dialog'
import { Button } from './ui/button'
import { FloatingInput, FloatingTextarea, SearchInput } from './ui/input'
import { Badge, StatusBox } from './ui/badge'
import { EmptyState } from './ui/empty-state'

type Props = {
  onClose: () => void
  onCreated: (project: ProjectDto) => void
}

const DEFAULT_PROMPT =
  'Read the project so you understand the codebase, then propose 2–3 small wins for what to build next.'

const FORM_ID = 'import-gitlab-form'

export function ImportFromGitlabModal({ onClose, onCreated }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const [repos, setRepos] = useState<GitlabRepoDto[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<GitlabRepoDto | null>(null)
  const [name, setName] = useState('')
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!accessToken) return
    api
      .gitlab.listRepos(accessToken)
      .then((res) => setRepos(res.repos))
      .catch((err: ApiError) => setLoadError(err.title ?? 'Failed to load repos'))
  }, [accessToken])

  const filtered = useMemo(() => {
    if (!repos) return []
    const q = filter.trim().toLowerCase()
    if (!q) return repos
    return repos.filter((r) =>
      r.pathWithNamespace.toLowerCase().includes(q) || r.name.toLowerCase().includes(q),
    )
  }, [repos, filter])

  function pickRepo(r: GitlabRepoDto): void {
    setSelected(r)
    // Default project name = the last path segment (the repo name).
    if (!name) setName(r.name)
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!accessToken || !selected) return
    setSubmitError(null)
    setSubmitting(true)
    try {
      const project = await api.createProject(accessToken, {
        name: name.trim() || selected.name,
        initialPrompt: prompt.trim() || DEFAULT_PROMPT,
        stack: 'nextjs-pg',
        gitlabSource: {
          repoId: selected.id,
          pathWithNamespace: selected.pathWithNamespace,
          repoUrl: selected.webUrl,
          defaultBranch: selected.defaultBranch ?? 'main',
        },
      })
      onCreated(project)
    } catch (err) {
      setSubmitError((err as ApiError).title ?? 'Failed to import')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="Import from GitLab"
      description={
        selected
          ? `Importing ${selected.pathWithNamespace}`
          : 'Pick a repo to import — Doable will clone it into a fresh workspace.'
      }
      footer={
        selected ? (
          <>
            <Button variant="ghost" onClick={() => setSelected(null)}>
              <ArrowLeft /> Back
            </Button>
            <Button type="submit" form={FORM_ID} disabled={submitting}>
              {submitting ? 'Importing…' : 'Import'}
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        )
      }
    >
      {!selected && (
        <div className="flex max-h-[60vh] flex-col gap-3">
          <SearchInput
            type="search"
            icon={<Search />}
            placeholder="Filter repos…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <div className="min-h-0 flex-1 overflow-y-auto rounded-xl ring-1 ring-foreground/10">
            {loadError && <StatusBox tone="destructive" className="m-3">{loadError}</StatusBox>}
            {!loadError && repos === null && (
              <div className="space-y-2 p-3">
                <div className="dwlt-skeleton h-10" />
                <div className="dwlt-skeleton h-10" />
                <div className="dwlt-skeleton h-10" />
              </div>
            )}
            {!loadError && repos !== null && filtered.length === 0 && (
              <EmptyState
                className="m-3"
                icon={<GitBranch />}
                title={filter ? 'No matches' : 'No repos found'}
                body={filter ? 'Try a different filter.' : 'Your GitLab account has no repositories Doable can see.'}
              />
            )}
            <ul className="divide-y">
              {filtered.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => pickRepo(r)}
                    className="flex w-full items-baseline justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{r.pathWithNamespace}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-mono">{r.defaultBranch ?? '—'}</span> · {r.visibility} · last activity{' '}
                        {r.lastActivityAt.slice(0, 10)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {selected && (
        <form id={FORM_ID} className="space-y-4 pt-1" onSubmit={onSubmit}>
          <FloatingInput
            label="Project name"
            type="text"
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={selected.name}
          />
          <FloatingTextarea
            label="First message to the agent"
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={4000}
            hint="Sent on the first build. The agent reads the cloned repo before responding."
          />
          <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
            <span>Repo <span className="font-mono text-foreground">{selected.pathWithNamespace}</span></span>
            <span>·</span>
            <span>Branch <span className="font-mono text-foreground">{selected.defaultBranch ?? 'main'}</span></span>
            <span>·</span>
            <Badge variant="secondary">{selected.visibility}</Badge>
          </div>
          {submitError && <StatusBox tone="destructive">{submitError}</StatusBox>}
        </form>
      )}
    </Dialog>
  )
}
